import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { aclCanActivate } from '@delon/acl';
import { authSimpleCanActivate } from '@delon/auth';
import { SEModule } from '@delon/abc/se';
import { SharedModule } from '@shared';

// layout
// single pages
import { LayoutBasicComponent } from '../layout/basic/basic.component';
import { LayoutBlankComponent } from '../layout/blank/blank.component';
import { LayoutPassportComponent } from '../layout/passport/passport.component';
import { sampleOrTrialAgentBlockGuard } from './ai/_guards/sample-or-trial-agent-block.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard'
  },
  {
    path: '',
    component: LayoutBasicComponent,
    canActivate: [authSimpleCanActivate],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'inquiry',
        loadChildren: () =>
          import('./inquiry/inquiry.module').then(m => m.InquiryModule)
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./dashboard/dashboard.module').then(m => m.DashboardModule)
      },
      {
        path: 'my-apps',
        canActivate: [aclCanActivate],
        loadComponent: () =>
          import('./dashboard/my-apps-page/my-apps-page.component').then(
            m => m.DashboardMyAppsPageComponent
          ),
        data: { title: 'My Apps' }
      },
      {
        path: 'leads',
        canActivate: [sampleOrTrialAgentBlockGuard],
        loadChildren: () =>
          import('./leads/leads.module').then(m => m.LeadsModule)
      },
      {
        path: 'customer-agent',
        canActivate: [sampleOrTrialAgentBlockGuard],
        loadChildren: () =>
          import('./customer-agent/customer-agent.module').then(
            m => m.CustomerAgentModule
          )
      },
      {
        path: 'exception',
        loadChildren: () =>
          import('./exception/exception.module').then(m => m.ExceptionModule)
      },
      {
        path: 'account',
        loadChildren: () =>
          import('./account/account.module').then(m => m.AccountModule)
      },
      {
        path: 'company',
        loadChildren: () =>
          import('./company/company.module').then(m => m.CompanyModule)
      },
      {
        path: 'banking',
        loadChildren: () =>
          import('./banking/banking.module').then(m => m.BankingModule)
      },
      {
        path: 'gl',
        loadChildren: () => import('./gl/gl.module').then(m => m.GLModule)
      },
      {
        path: 'ar',
        loadChildren: () => import('./ar/ar.module').then(m => m.ARModule)
      },
      {
        path: 'ap',
        loadChildren: () => import('./ap/ap.module').then(m => m.APModule)
      },
      {
        path: 'sale',
        loadChildren: () => import('./sale/sale.module').then(m => m.SaleModule)
      },
      {
        path: 'purchase',
        loadChildren: () =>
          import('./purchase/purchase.module').then(m => m.PurchaseModule)
      },
      {
        path: 'tax',
        loadChildren: () => import('./tax/tax.module').then(m => m.TaxModule)
      },
      {
        path: 'stk',
        loadChildren: () =>
          import('./stocks/stocks.module').then(m => m.StockModule)
      },
      {
        path: 'bir',
        loadChildren: () => import('./bir/bir.module').then(m => m.BIRModule)
      },
      {
        path: 'ai',
        loadChildren: () => import('./ai/ai.module').then(m => m.AIModule)
      },
      {
        path: 'einv',
        loadChildren: () =>
          import('./einv/einvoice.module').then(m => m.EInvoiceModule)
      },
      {
        path: 'search',
        loadChildren: () =>
          import('./search/search.module').then(m => m.SearchModule)
      },
      {
        path: 'marketplace',
        loadChildren: () =>
          import('./marketplace/marketplace.module').then(
            m => m.MarketplaceModule
          )
      },
      {
        path: 'ai-studio',
        loadChildren: () =>
          import('./erp-app-builder/ai-studio.module').then(m => m.AiStudioModule)
      },
      {
        path: 'ai-studio/mcp-connections',
        pathMatch: 'full',
        redirectTo: 'ai/mcp-connections'
      },
      {
        path: 'erp-app-builder',
        pathMatch: 'full',
        redirectTo: 'ai-studio/app-builder'
      },
      {
        path: 'erp-app-builder/my-apps',
        pathMatch: 'full',
        redirectTo: 'ai-studio/app-builder'
      },
      {
        path: 'erp-app-builder/webhooks',
        pathMatch: 'full',
        redirectTo: 'ai-studio/webhooks'
      },
      {
        path: 'integrator',
        loadChildren: () =>
          import('./integrator/integrator.module').then(m => m.IntegratorModule)
      },
      {
        path: 'peppol',
        loadChildren: () =>
          import('./peppol/peppol.module').then(m => m.PeppolModule)
      },
      {
        path: 'pkey-migration',
        data: { title: 'Migrate to Subscription' },
        loadComponent: () =>
          import('./ai/pkey-migration/pkey-migration.component').then(c => c.PkeyMigrationComponent)
      }
    ]
  },
  // Reporting module
  {
    path: 'reporting',
    loadChildren: () =>
      import('./reporting/reporting.module').then(m => m.ReportingModule)
  },
  // blank layout
  {
    path: 'full',
    component: LayoutBlankComponent,
    children: [
      {
        path: 'setup',
        loadChildren: () =>
          import('./setup/setup.module').then(m => m.SetupModule)
      },
      {
        path: 'view',
        loadChildren: () => import('./view/view.module').then(m => m.ViewModule)
      },
      {
        path: 'einvoice',
        loadChildren: () =>
          import('./einvoice/einvoice-portal.module').then(
            m => m.EInvoicePortalModule
          )
      },
      {
        path: 'portal',
        loadChildren: () =>
          import('./payment-portal/payment-portal.module').then(
            m => m.PaymentPortalModule
          )
      },
      {
        path: 'bir-portal',
        loadChildren: () =>
          import('./bir-portal/bir-portal.module').then(
            m => m.BIRPortalModule
          )
      },
      {
        path: 'ai/shared/:shareId',
        loadComponent: () =>
          import('./ai/shared-conversation/shared-conversation.component').then(
            m => m.SharedConversationComponent
          ),
        data: { title: 'Shared Conversation' }
      },
      {
        path: 'telegram-whitelist-approve',
        loadComponent: () =>
          import('./ai/telegram-whitelist-approve/telegram-whitelist-approve.component').then(
            m => m.TelegramWhitelistApproveComponent
          ),
        data: { title: 'Approve Telegram Contact', reuse: false }
      },
      {
        path: 'partner/subscription',
        loadComponent: () =>
          import('./partner-subscription/partner-subscription-entry.component').then(
            m => m.PartnerSubscriptionEntryComponent
          ),
        data: { title: 'Partner Subscription', reuse: false }
      },
      {
        path: 'partner/subscription/manage',
        loadComponent: () =>
          import('./partner-subscription/partner-subscription-manage.component').then(
            m => m.PartnerSubscriptionManageComponent
          ),
        data: { title: 'Manage Plan', reuse: false }
      }
    ]
  },
  // passport
  {
    path: 'auth/google/callback',
    loadComponent: () =>
      import('./passport/google-callback/google-callback.component').then(
        m => m.GoogleCallbackComponent
      ),
    data: { reuse: false, title: 'Google sign-in' }
  },
  {
    path: 'auth/msal/callback',
    loadComponent: () =>
      import('./passport/entra-callback/entra-callback.component').then(
        m => m.EntraCallbackComponent
      ),
    data: { reuse: false, title: 'Microsoft sign-in' }
  },
  {
    path: 'passport',
    component: LayoutPassportComponent,
    loadChildren: () =>
      import('./passport/passport.module').then(m => m.PassportModule)
  },
  // Page without Layout
  {
    path: 'passport/callback',
    loadComponent: () =>
      import('./passport/callback/callback.component').then(
        m => m.CallbackComponent
      ),
    data: { reuse: false, title: 'Callback' }
  },
  {
    path: 'passport/update-profile',
    loadComponent: () =>
      import('./passport/update-profile/update-profile.component').then(
        m => m.UpdateProfileComponent
      ),
    data: { reuse: false, title: 'Complete Profile' }
  },
  {
    path: 'passport/entra-complete-profile',
    redirectTo: 'passport/update-profile',
    pathMatch: 'full'
  },
  {
    path: 'passport/complete-signup',
    loadChildren: () =>
      import('./passport/complete-signup.module').then(
        m => m.CompleteSignupModule
      )
  },
  {
    path: 'passport/set-password',
    loadComponent: () =>
      import('./passport/set-password/set-password.component').then(
        m => m.SetPasswordComponent
      ),
    data: { reuse: false, title: 'Reset Password' }
  },

  {
    path: '**',
    loadComponent: () =>
      import('./page-not-found.component').then(m => m.PageNotFoundComponent),
    data: { reuse: false }
  }
];

@NgModule({
  imports: [
    SharedModule,
    SEModule,
    RouterModule.forRoot(routes, {
      useHash: false,
      // NOTICE: If you use `reuse-tab` component and turn on keepingScroll you can set to `disabled`
      // Pls refer to https://ng-alain.com/components/reuse-tab
      scrollPositionRestoration: 'enabled'
    })
  ],
  declarations: [],
  exports: [RouterModule]
})
export class RoutesModule {}
